<template>
  <sg-tooltip
    ref="popper"
    :show-arrow = "false"
    v-model:visible="suggestionVisible"
    :placement="placement"
    :fallback-placements="['bottom-start', 'top-start']"
    :gpu-acceleration="false"
    :drop-class-name="`${ns.e('popper')}`"
    :transfer="transfer"
    pure
    manual-mode
    theme="light"
    trigger="click"
    :transition="`${ns.namespace.value}-zoom-in-top`"
    persistent
    @before-show="onSuggestionShow"
    @show="onShow"
    @hide="onHide"
  >
    <div
      v-clickoutside="close"
      :class="[ns.b(), $attrs.class]"
      :style="$attrs.style as any || undefined"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="suggestionVisible"
      :aria-owns="id"
    >
      <sg-input
        ref="inputRef"
        v-bind="attrs"
        type="text"
        :size="size"
        :model-value="modelValue"
        @on-input="handleInput"
        @on-change="handleChange"
        @on-focus="handleFocus"
        @on-blur="handleBlur"
        @on-clear="handleClear"
        @keydown.up.prevent="highlight(highlightedIndex - 1)"
        @keydown.down.prevent="highlight(highlightedIndex + 1)"
        @keydown.enter="handleKeyEnter"
        @keydown.tab="close"
        @keydown.esc="handleKeyEscape"
      >
        <template v-if="$slots.prepend" #prepend>
          <slot name="prepend"></slot>
        </template>
        <template v-if="$slots.append" #append>
          <slot name="append"></slot>
        </template>
      </sg-input>
    </div>
    <template #content>
      <div
        ref="regionRef"
        :class="[ns.b('suggestion'), ns.is('loading', suggestionLoading)]"
        :style="{ minWidth: scrollwidth ? '' : dropdownWidth, outline: 'none', width: scrollwidth }"
        role="region"
      >
        <sg-scrollbar
          :id="id"
          tag="ul"
          :wrap-class="ns.be('suggestion', 'wrap')"
          :view-class="ns.be('suggestion', 'list')"
          :wrap-style="{ maxHeight: scrollheight }"
          role="listbox"
        >
          <li v-if="suggestionLoading">
            <sg-icon class="is-loading"><Loading /></sg-icon>
          </li>
          <template v-else>
            <li
              v-for="(item, index) in suggestions"
              :id="`${id}-item-${index}`"
              :key="index"
              :class="{ highlighted: highlightedIndex === index }"
              role="option"
              :aria-selected="highlightedIndex === index"
              @click="select(item)"
            >
              <slot :item="item">{{ item[valueKey] }}</slot>
            </li>
          </template>
        </sg-scrollbar>
      </div>
    </template>
  </sg-tooltip>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, nextTick } from 'vue'
import debounce from 'lodash/debounce'
import { useAttrs, useNamespace } from '@sgui-plus/hooks'
import { ClickOutside } from '@sgui-plus/directives'
import { generateId, isArray } from '@sgui-plus/utils/util'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { throwError } from '@sgui-plus/utils/error'
import SgInput from '@sgui-plus/components/input'
import SgScrollbar from '@sgui-plus/components/scrollbar'
import SgTooltip from '@sgui-plus/components/tooltip'
import SgIcon from '@sgui-plus/components/icon'
import { Loading } from '@sgui-plus/icons-vue'
import { autocompleteProps, autocompleteEmits } from './autocomplete'
import type { AutocompleteData } from './autocomplete'

export default defineComponent({
  name: 'SgAutocomplete',
  components: {
    SgTooltip,
    SgInput,
    SgScrollbar,
    SgIcon,
    Loading,
  },
  directives: {
    clickoutside: ClickOutside,
  },
  inheritAttrs: false,
  props: autocompleteProps,
  emits: autocompleteEmits,
  setup(props, ctx) {
    const ns = useNamespace('autocomplete')
    const attrs = useAttrs()
    const suggestions = ref<any[]>([])
    const highlightedIndex = ref(-1)
    const dropdownWidth = ref('')
    const activated = ref(false)
    const suggestionDisabled = ref(false)
    const loading = ref(false)
    let isClear = false
    let ignoreFocusEvent = false
    const inputRef = ref<{
      inputOrTextarea: HTMLInputElement | HTMLTextAreaElement
      focus: () => void
      $el: HTMLElement
    } | null>(null)
    const regionRef = ref<HTMLElement | null>(null)
    const popper = ref(null)

    const id = computed(() => {
      return ns.b(String(generateId()))
    })
    const suggestionVisible = computed(() => {
      const isValidData =
        isArray(suggestions.value) && suggestions.value.length > 0
      return (isValidData || loading.value) && activated.value
    })
    const suggestionLoading = computed(() => {
      return !props.hideLoading && loading.value
    })

    const onSuggestionShow = async () => {
      await nextTick()
      if (suggestionVisible.value) {
        dropdownWidth.value = `${inputRef.value!.$el.offsetWidth}px`
      }
    }

    const onShow = () => {
      ignoreFocusEvent = true
    }

    const onHide = () => {
      ignoreFocusEvent = false
      highlightedIndex.value = -1
    }

    onMounted(() => {
      inputRef.value!.inputOrTextarea.setAttribute('role', 'textbox')
      inputRef.value!.inputOrTextarea.setAttribute('aria-autocomplete', 'list')
      inputRef.value!.inputOrTextarea.setAttribute('aria-controls', 'id')
      inputRef.value!.inputOrTextarea.setAttribute(
        'aria-activedescendant',
        `${id.value}-item-${highlightedIndex.value}`
      )
    })

    const getData = async (queryString: string) => {
      if (suggestionDisabled.value) {
        return
      }

      const cb = (suggestionList: AutocompleteData) => {
        loading.value = false
        if (suggestionDisabled.value) return

        if (isArray(suggestionList)) {
          suggestions.value = suggestionList
          highlightedIndex.value = props.highlightFirstItem ? 0 : -1
        } else {
          throwError('SgAutocomplete', 'autocomplete suggestions must be an array')
        }
      }
      loading.value = true
      if (isArray(props.fetchSuggestions)) {
        cb(props.fetchSuggestions)
      } else {
        const result = await props.fetchSuggestions(queryString, cb)
        if (isArray(result)) cb(result)
      }
    }

    const debouncedGetData = debounce(getData, props.debounce)

    const handleInput = (value: string) => {
      const valuePresented = !!value
      ctx.emit(UPDATE_MODEL_EVENT, value)

      suggestionDisabled.value = false
      activated.value ||= isClear && valuePresented

      if (!props.triggerOnFocus && !value) {
        suggestionDisabled.value = true
        suggestions.value = []
        return
      }
      if (isClear && valuePresented) {
        isClear = false
      }

      debouncedGetData(value)
    }

    const handleChange = (value: string) => {
      ctx.emit('onChange', value)
    }

    const handleFocus = (e: FocusEvent) => {
      if (ignoreFocusEvent) return

      activated.value = true
      ctx.emit('onFocus', e)
      if (props.triggerOnFocus) {
        debouncedGetData(String(props.modelValue))
      }
    }

    const handleBlur = (e: FocusEvent) => {
      if (ignoreFocusEvent) return
      ctx.emit('onBlur', e)
    }

    const handleClear = () => {
      activated.value = false
      isClear = true
      ctx.emit(UPDATE_MODEL_EVENT, '')
      ctx.emit('onClear')
    }

    const handleKeyEnter = () => {
      if (
        suggestionVisible.value &&
        highlightedIndex.value >= 0 &&
        highlightedIndex.value < suggestions.value.length
      ) {
        select(suggestions.value[highlightedIndex.value])
      } else if (props.selectWhenUnmatched) {
        ctx.emit('onSelect', { value: props.modelValue })
        nextTick(() => {
          suggestions.value = []
          highlightedIndex.value = -1
        })
      }
    }

    const handleKeyEscape = (evt: Event) => {
      if (suggestionVisible.value) {
        evt.preventDefault()
        evt.stopPropagation()
        close()
      }
    }

    const close = () => {
      activated.value = false
    }

    const focus = () => {
      inputRef.value?.focus()
    }

    const select = (item: any) => {
      ctx.emit('onInput', item[props.valueKey])
      ctx.emit(UPDATE_MODEL_EVENT, item[props.valueKey])
      ctx.emit('onSelect', item)
      nextTick(() => {
        suggestions.value = []
        highlightedIndex.value = -1
      })
    }
    const highlight = (index: number) => {
      if (!suggestionVisible.value || loading.value) {
        return
      }
      if (index < 0) {
        highlightedIndex.value = -1
        return
      }
      if (index >= suggestions.value.length) {
        index = suggestions.value.length - 1
      }
      const suggestion = regionRef.value!.querySelector(
        `.${ns.be('suggestion', 'wrap')}`
      )!
      const suggestionList = suggestion.querySelectorAll(
        `.${ns.be('suggestion', 'list')} li`
      )!
      const highlightItem = suggestionList[index]
      const scrollTop = suggestion.scrollTop
      const { offsetTop, scrollHeight } = highlightItem as HTMLElement

      if (offsetTop + scrollHeight > scrollTop + suggestion.clientHeight) {
        suggestion.scrollTop += scrollHeight
      }
      if (offsetTop < scrollTop) {
        suggestion.scrollTop -= scrollHeight
      }
      highlightedIndex.value = index
      inputRef.value!.inputOrTextarea.setAttribute(
        'aria-activedescendant',
        `${id.value}-item-${highlightedIndex.value}`
      )
    }

    return {
      attrs,
      suggestions,
      highlightedIndex,
      dropdownWidth,
      activated,
      suggestionDisabled,
      loading,
      inputRef,
      regionRef,
      popper,

      id,
      suggestionVisible,
      suggestionLoading,

      getData,
      handleInput,
      handleChange,
      handleFocus,
      handleBlur,
      handleClear,
      handleKeyEnter,
      handleKeyEscape,
      close,
      focus,
      select,
      highlight,
      onSuggestionShow,
      onShow,
      onHide,
      ns,
    }
  },
})
</script>
